import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {SaccoGoal} from "@/@types/sacco.models.ts";

/**
 * SaccoGoalsService provides methods to manage and retrieve Sacco contribution goals for a given user and membership.
 * It extends the BaseService to utilize common service functionalities.
 */
export class SaccoGoalsService extends BaseService {

    /**
     * Creates a new contribution goal for the sacco.
     *
     * @param {string} userId - The ID of the user associated with the contribution goal.
     * @param {SaccoContributionGoalRequest} request - The request object containing details for the contribution goal.
     * @return {Promise<SaccoGoal[]>} A promise that resolves to an array of SaccoGoal objects after successfully
     * creating a contribution goal.
     */
    public async createContributionGoal(userId: string, request: SaccoContributionGoalRequest): Promise<SaccoGoal> {
        try {
            const payload = this.convertRequestToSnakeCase(request);
            const { data, error } = await supabase
                .from('sacco_contribution_goals')
                .insert({...payload, user_id: userId})
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error creating Sacco contribution goal", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches Sacco contribution goals for a specific user and membership.
     *
     * @param {string} userId - The unique identifier of the user.
     * @param {string} membershipId - The unique identifier of the membership.
     * @return {Promise<SaccoGoal[]>} A promise that resolves to an array of SaccoGoal objects.
     */
    public async fetchGoals(userId: string, membershipId: string): Promise<SaccoGoal[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_contribution_goals')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('target_date', { ascending: true });
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error fetching Sacco contribution goals", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates an existing contribution goal in the "sacco_contribution_goals" table.
     *
     * @param {SaccoContributionGoalUpdateRequest} request - The request object containing the updated goal details.
     * It must include the goal id and the fields to update.
     *
     * @return {Promise<SaccoGoal>} A promise that resolves to the updated contribution goal object.
     * Throws an error if the update operation fails.
     */
    public async updateContributionGoal(request: SaccoContributionGoalUpdateRequest): Promise<SaccoGoal> {
        try {
            const payload = this.convertRequestToSnakeCase(request);
            const { data, error } = await supabase
                .from('sacco_contribution_goals')
                .update(payload)
                .eq('id', request.id)
                .select()
                .single();
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error updating Sacco contribution goal", error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a contribution goal from the database based on the provided ID.
     *
     * @param {string} id - The unique identifier of the contribution goal to delete.
     * @return {Promise<void>} A promise that resolves when the deletion is complete.
     */
    public async deleteContributionGoal(id: string): Promise<void> {
        try {
            const { error } = await supabase.from('sacco_contribution_goals').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting Sacco contribution goal", error);
            await this.handleError(error);
        }
    }
}

export default new SaccoGoalsService();
